import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import ProfileSection from '../components/profile/ProfileSection';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { normalizeCity } from '@/lib/city/normalizer';

const Profile: React.FC = () => {
  const location     = useLocation();
  const navigate     = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const urlTab       = searchParams.get('tab');
  const { isTrainer } = useCurrentUser();

  /* ─────────── normalise optional `city` param once on mount */
  const cityParam  = searchParams.get('city');
  const normalCity = cityParam ? normalizeCity(cityParam) : null;

  useEffect(() => {
    if (cityParam && normalCity && normalCity !== cityParam) {
      const params = new URLSearchParams(location.search);
      params.set('city', normalCity);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─────────── URL-push for trainers so they default to trainer tab */
  useEffect(() => {
    // if user is a trainer and no explicit tab is set, force ?tab=trainer
    if (isTrainer && !urlTab) {
      const params = new URLSearchParams(location.search);
      params.set('tab', 'trainer');
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [isTrainer, urlTab, location.pathname, navigate]);

  /* ─────────── tab state (trainers default to “trainer”, others to “posts”) */
  const locationState = location.state as { activeTab?: string } | null;

  const initialTab = (
    urlTab ||
    locationState?.activeTab ||
    (isTrainer ? 'trainer' : 'posts')
  ) as string;

  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const newTab = new URLSearchParams(location.search).get('tab');
    if (newTab && newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search, activeTab]);

  const handleTabChange = (tab: string) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`?${params.toString()}`, { replace: true });
    setActiveTab(tab);
  };

  /* ─────────── render */
  return (
    <DashboardLayout>
      <ProfileSection
        defaultTab={activeTab}
        onTabChange={handleTabChange}
      />
    </DashboardLayout>
  );
};

export default Profile;
